import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

gsap.registerPlugin(MotionPathPlugin);

export { gsap };

// Light travelling along a wire: a bright streak plus a glowing head.
export function travel(tl, q, id, at, duration = 0.7) {
  const wire = q(`[data-wire="${id}"]`)[0];
  const streak = q(`[data-streak="${id}"]`);
  const head = q(`[data-head="${id}"]`);
  tl.set([streak, head], { opacity: 1 }, at)
    .fromTo(streak, { strokeDashoffset: 0.3 }, { strokeDashoffset: -1, duration, ease: 'power1.inOut' }, at)
    .to(head, { motionPath: { path: wire, align: wire, alignOrigin: [0.5, 0.5] }, duration, ease: 'power1.inOut' }, at)
    .to([streak, head], { opacity: 0, duration: 0.18 }, at + duration - 0.06);
  return tl;
}

// A pill's accent ring flares and settles.
export function flare(tl, q, id, at, hold = 0.35) {
  const ring = q(`[data-ring="${id}"]`);
  tl.fromTo(ring, { opacity: 0 }, { opacity: 1, duration: 0.18, ease: 'power2.out' }, at)
    .to(ring, { opacity: 0, duration: 0.7, ease: 'power2.inOut' }, at + 0.18 + hold);
  return tl;
}

// Joints blink as light passes through them.
export function blink(tl, q, ids, at) {
  tl.fromTo(q(ids.map(id => `[data-joint="${id}"]`).join(',')), { scale: 1, transformOrigin: '50% 50%' },
    { scale: 2, duration: 0.16, yoyo: true, repeat: 1, ease: 'power2.out' }, at);
  return tl;
}

// The core absorbs a pulse: halo bursts, face brightens, icon pops.
export function charge(tl, q, at) {
  tl.fromTo(q('[data-core-halo]'), { opacity: 0.35, scale: 1 }, { opacity: 1, scale: 1.25, duration: 0.22, ease: 'power2.out' }, at)
    .to(q('[data-core-halo]'), { opacity: 0.35, scale: 1, duration: 0.9, ease: 'power2.inOut' }, at + 0.22)
    .fromTo(q('[data-core-face] > svg'), { scale: 1 }, { scale: 1.14, duration: 0.18, yoyo: true, repeat: 1, ease: 'power2.out' }, at);
  return tl;
}

// Always-on ambience: drifting glows, rotating rim light, beam breathing, sparks.
export function ambience(q) {
  q('[data-glow]').forEach((glow, i) => gsap.to(glow, {
    xPercent: -50 + (i % 2 ? 8 : -8), yPercent: -50 + (i % 2 ? -6 : 6), scale: 1.12,
    duration: 5 + i, ease: 'sine.inOut', yoyo: true, repeat: -1,
  }));
  const sweep = q('[data-core-sweep]');
  const beam = q('[data-beam]');
  if (sweep.length) gsap.to(sweep, { rotate: 360, duration: 6, ease: 'none', repeat: -1 });
  if (beam.length) gsap.to(beam, { opacity: 0.8, duration: 2.6, ease: 'sine.inOut', yoyo: true, repeat: -1 });
  q('[data-spark]').forEach(spark => {
    const duration = gsap.utils.random(1.6, 2.6);
    gsap.timeline({ repeat: -1, delay: gsap.utils.random(0, 2), repeatDelay: gsap.utils.random(0.2, 1.4) })
      .fromTo(spark, { y: 0 }, { y: -gsap.utils.random(18, 40), duration, ease: 'sine.out' }, 0)
      .fromTo(spark, { opacity: 0 }, { opacity: 1, duration: duration * 0.3 }, 0)
      .to(spark, { opacity: 0, duration: duration * 0.5 }, duration * 0.5);
  });
}

// Entrance: wires draw in while parts rise into place.
export function entrance(q, tl = gsap.timeline()) {
  tl.from(q('[data-wire]'), { strokeDashoffset: 1, duration: 0.9, ease: 'power2.inOut', stagger: 0.05 }, 0)
    .from(q('[data-enter]'), { opacity: 0, y: 8, duration: 0.55, ease: 'power3.out', stagger: 0.05 }, 0.05)
    .from(q('[data-core]'), { opacity: 0, scale: 0.86, duration: 0.7, ease: 'back.out(1.6)' }, 0.15)
    .from(q('[data-joint]'), { scale: 0, transformOrigin: '50% 50%', duration: 0.3, stagger: 0.03 }, 0.6);
  return tl;
}
